/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from '@emotion/react';
import {
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
} from '@mui/material';
import { FC, Fragment, ReactNode, useState } from 'react';
import { BORDER_COLOR } from '../../../typings';
import QSButton from '../../atoms/button';
import QSTypography from '../../atoms/typography';
import QSIconButton from '../../atoms/icon-button';
import { CloseRounded } from '@mui/icons-material';
import DialogWrapper from '../../molecules/url-popup';
import Loader from '../../molecules/loader';

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (() => Promise<any>) | (() => void);
  message?: ReactNode;
  buttonText?: string;
  secondaryButtonText?: string;
  primaryButtonType?: 'primary' | 'danger';
  secondaryButtonProps?: Omit<ButtonProps, 'css' | 'variant'>;
  secondaryButtonTextStyles?: SerializedStyles;
  primaryButtonProps?: Omit<ButtonProps, 'css'>;
  primaryButtonTextStyles?: SerializedStyles;
  title?: ReactNode;
  icon?: ReactNode;
  contentContainerStyles?: SerializedStyles;
  children?: ReactNode;
  showActions?: boolean;
  dialogProps?: Omit<DialogProps, 'open' | 'onClose'>;
  closeOnSubmit?: boolean;
  titleStyles?: SerializedStyles;
  actions?: ReactNode;
  onlyCloseWithButton?: boolean;
}

const ConfirmModal: FC<ConfirmModalProps> = ({
  open,
  onClose,
  onSubmit,
  message,
  buttonText,
  secondaryButtonText,
  primaryButtonType,
  secondaryButtonProps,
  secondaryButtonTextStyles,
  primaryButtonProps,
  primaryButtonTextStyles,
  title,
  icon,
  contentContainerStyles,
  children,
  showActions = true,
  dialogProps,
  closeOnSubmit = true,
  titleStyles,
  actions,
  onlyCloseWithButton = false,
}) => {
  const [loading, setLoading] = useState(false);

  const handleonSubmit = async () => {
    if (typeof onSubmit !== 'function') {
      return;
    }
    setLoading(true);
    try {
      await onSubmit();
      setLoading(false);
      if (closeOnSubmit && !onlyCloseWithButton) onClose();
    } catch (error) {
      setLoading(false);
    }
  };

  const renderDialogTitle = () => {
    if (!title || (typeof title === 'string' && title.length === 0)) {
      return null;
    }
    return (
      <Fragment>
        <DialogTitle
          css={[
            css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              column-gap: 12px;
              padding: 16px 16px 16px 24px;
            `,
            titleStyles,
          ]}
        >
          {typeof title === 'string' || typeof title === 'number' ? (
            <QSTypography
              css={css`
                font-size: 18px;
                font-weight: 600;
                word-wrap: break-word;
                word-break: break-word;
              `}
            >
              {title}
            </QSTypography>
          ) : (
            title
          )}
          <QSIconButton sx={{ alignSelf: 'flex-start' }} onClick={onClose}>
            <CloseRounded />
          </QSIconButton>
        </DialogTitle>
        <Divider
          variant={'fullWidth'}
          sx={{
            borderColor: BORDER_COLOR,
          }}
        />
      </Fragment>
    );
  };

  return (
    <DialogWrapper
      open={open}
      onClose={!onlyCloseWithButton ? onClose : () => {}}
      dialogProps={{ disableEscapeKeyDown: loading, ...dialogProps }}
    >
      {renderDialogTitle()}
      <DialogContent
        css={css(
          `
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        column-gap: 12px;
      `,
          contentContainerStyles
        )}
      >
        {children ? (
          children
        ) : (
          <Fragment>
            {icon ? icon : null}
            {message && typeof message !== 'string' ? (
              message
            ) : (
              <QSTypography
                css={css`
                  font-size: 16px;
                  font-weight: 400;
                  word-wrap: break-word;
                  word-break: break-word;
                `}
              >
                {message || `Are you sure you want to delete?`}
              </QSTypography>
            )}
          </Fragment>
        )}
      </DialogContent>
      <Divider
        variant="fullWidth"
        sx={{
          borderColor: BORDER_COLOR,
        }}
      />
      {showActions && (
        <DialogActions
          css={css`
            padding: 12px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            column-gap: 12px;

            & > button:not(:first-of-type) {
              margin: 0px;
            }
          `}
        >
          {actions ? (
            actions
          ) : (
            <>
              <QSButton
                variant="text"
                css={css`
                  margin: 0px;
                  padding: 6px 16px;
                `}
                onClick={onClose}
                {...secondaryButtonProps}
              >
                <QSTypography
                  css={css(
                    `
                  color: #757575;
                  font-size: 16px;
                  font-weight: 400;
                  text-transform: uppercase;
                  word-wrap: break-word;
                  word-break: break-word;
                `,
                    secondaryButtonTextStyles
                  )}
                >
                  {secondaryButtonText ?? `Cancel`}
                </QSTypography>
              </QSButton>
              <QSButton
                variant={primaryButtonProps?.variant ?? 'contained'}
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  column-gap: 8px;
                  margin: 0px;
                  padding: 6px 16px;
                  background: ${primaryButtonType === 'danger'
                    ? '#ef5350'
                    : 'rgba(63,144,109, 1)'};
                  &:hover {
                    background: ${primaryButtonType === 'danger'
                      ? '#e53935'
                      : 'rgb(44, 100, 76)'};
                  }
                `}
                onClick={handleonSubmit}
                {...primaryButtonProps}
                disabled={loading || primaryButtonProps?.disabled}
              >
                <QSTypography
                  css={css(
                    `
                  font-size: 16px;
                  font-weight: 400;
                  text-transform: uppercase;
                  word-wrap: break-word;
                  word-break: break-word;
                `,
                    primaryButtonTextStyles
                  )}
                >
                  {buttonText ?? `Delete`}
                </QSTypography>
                {loading && (
                  <Loader
                    size={18}
                    containerProps={{
                      style: {
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      },
                    }}
                    loaderProps={{
                      style: {
                        color: 'white',
                      },
                    }}
                  />
                )}
              </QSButton>
            </>
          )}
        </DialogActions>
      )}
    </DialogWrapper>
  );
};

export default ConfirmModal;
